import { useCallback, useEffect, useState } from "react";
import { apiClient } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Loader2, Navigation, Phone } from "lucide-react";
import RideInfo from "@/components/calendar/RideInfo";
import AssignSection from "@/components/calendar/AssignSection";
import RescheduleSection from "@/components/calendar/RescheduleSection";
import { CancelSection, HistorySection } from "@/components/calendar/RideActions";
import useConflictGuard from "@/components/calendar/useConflictGuard";

const navUrl = (provider, address, lat, lng) => {
  const hasCoords = lat && lng;
  if (provider === "waze") return hasCoords ? `https://waze.com/ul?ll=${lat},${lng}&navigate=yes` : `https://waze.com/ul?q=${encodeURIComponent(address)}&navigate=yes`;
  return `https://www.google.com/maps/dir/?api=1&destination=${hasCoords ? `${lat},${lng}` : encodeURIComponent(address)}`;
};

const DriverNav = ({ ride }) => {
  const p = ride.pickup_locations?.[0] || {};
  const pref = localStorage.getItem("preferredNav") || "google";
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="detail-driver-nav">
      <Button onClick={() => window.open(navUrl(pref, p.address, p.latitude, p.longitude), "_blank")} className="bg-cyan-600 hover:bg-cyan-700" data-testid="detail-nav-pickup-btn">
        <Navigation className="w-4 h-4 mr-2" />Pickup
      </Button>
      <Button onClick={() => window.open(navUrl(pref, ride.dropoff_address, ride.dropoff_latitude, ride.dropoff_longitude), "_blank")} className="bg-orange-500 hover:bg-orange-600" data-testid="detail-nav-dropoff-btn">
        <Navigation className="w-4 h-4 mr-2" />Destination
      </Button>
      <Button variant="outline" onClick={() => window.open(`tel:${ride.client_phone}`)} data-testid="detail-call-customer-btn">
        <Phone className="w-4 h-4 mr-2" />Call Customer
      </Button>
    </div>
  );
};

export const RideDetailDialog = ({ rideId, onClose, mode = "dispatch", drivers = [], onChanged }) => {
  const [ride, setRide] = useState(null);
  const [version, setVersion] = useState(0);
  const { guard, conflictDialog } = useConflictGuard();

  const load = useCallback(async () => {
    if (!rideId) return;
    try {
      const { data } = await apiClient.get(`/rides/${rideId}`);
      setRide(data);
    } catch (e) {
      setRide({ error: e.response?.status === 403 ? "You do not have access to this ride." : "Ride not found." });
    }
  }, [rideId]);

  useEffect(() => { setRide(null); load(); }, [load]);

  const changed = () => {
    setVersion((v) => v + 1);
    load();
    onChanged?.();
  };

  return (
    <Dialog open={!!rideId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white text-slate-900" data-testid="ride-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{ride?.client_name || "Ride details"}</DialogTitle>
          <DialogDescription>Full booking information</DialogDescription>
        </DialogHeader>
        {!ride && <Loader2 className="w-6 h-6 animate-spin mx-auto my-8 text-cyan-600" />}
        {ride?.error && <p className="text-sm text-red-600" data-testid="ride-detail-error">{ride.error}</p>}
        {ride && !ride.error && (
          <div className="space-y-5">
            <RideInfo ride={ride} showFare={mode !== "driver" || ride.payment_method === "cash"} />
            {mode === "driver" && <DriverNav ride={ride} />}
            {mode === "dispatch" && (
              <Tabs defaultValue="assign">
                <TabsList className="grid grid-cols-4 w-full">
                  <TabsTrigger value="assign" data-testid="detail-tab-assign">Driver</TabsTrigger>
                  <TabsTrigger value="reschedule" data-testid="detail-tab-reschedule">Reschedule</TabsTrigger>
                  <TabsTrigger value="cancel" data-testid="detail-tab-cancel">Cancel</TabsTrigger>
                  <TabsTrigger value="history" data-testid="detail-tab-history">History</TabsTrigger>
                </TabsList>
                <TabsContent value="assign" className="pt-3"><AssignSection ride={ride} drivers={drivers} guard={guard} onChanged={changed} /></TabsContent>
                <TabsContent value="reschedule" className="pt-3"><RescheduleSection key={`${ride.id}-${version}`} ride={ride} guard={guard} onChanged={changed} /></TabsContent>
                <TabsContent value="cancel" className="pt-3"><CancelSection ride={ride} onChanged={changed} /></TabsContent>
                <TabsContent value="history" className="pt-3"><HistorySection rideId={ride.id} refreshKey={version} /></TabsContent>
              </Tabs>
            )}
          </div>
        )}
        {conflictDialog}
      </DialogContent>
    </Dialog>
  );
};

export default RideDetailDialog;
